import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import styles from "./CreateEngagementLetterWizard.module.css";
import WizardPageController from "../../reusable_components/WizardPageController/WizardPageController";
import { Modal, Row, Col } from "react-bootstrap";
import {
  prepareFieldsForMailMerge,
  CreateEngagementLetterWizardProps,
  createNewEngagementLetterComponentMap,
  EngagementLetterWizardSharedData,
  getUpdatedTemplateFieldsMapping,
  isClientFormPopulated,
  mergeElFormIntoSelectedFields,
  TemplateRuleDetailProps,
} from "./CreateEngagementLetterWizard.utils";
import { WizardPageConfig } from "../../reusable_components/WizardPageController/WizardPageController.utils";
import WizardHeaderPagination from "../../reusable_components/WizardHeaderPagination/WizardHeaderPagination";
import {
  fetchClientDetails,
  saveEngagementLetter,
  fetchWizardPageData,
  mailMerge,
  fetchProcessedTemplateClientDetails,
} from "./CreateEngagementLetterWizard.service";
import * as Icon from "react-bootstrap-icons";
import { WarningModal } from "./components/WarningModal/WarningModal";
import { isEqual } from "lodash";
import { DocumentEditorContainer } from "../CreateOrEditEditorComponent/CreateOrEditEditorComponent";
import { fetchAllClients } from "./components/ClientSelector/ClientSelector.service";
import CreateOrEditEditorComponent from "../CreateOrEditEditorComponent/CreateOrEditEditorComponent";
import { formatByConfig, getDisplayFmt } from "../CreateOrEditEditorComponent/CreateOrEditEditorComponent.utils";
import { applyNameFormat } from "../../../Reusables/components/InputFieldsController/InputFieldsController.utils";
import { format } from "date-fns";


/**
 * CreateEngagementLetterWizard Component
 *
 * This is a multi-step modal wizard used to create an engagement letter.
 * It includes client selection, template selection, form filling, and a document preview editor.
 */
const CreateEngagementLetterWizard: React.FC<
  CreateEngagementLetterWizardProps
> = ({
  show,
  onHide,
  setapiflag,
  clientMasterId,
  templateOptions,
  templatesFetched,
  isDraftEdit,
  draftProcessedTemplateId,
  draftTemplateId,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [sharedData, setSharedData] =
    useState<EngagementLetterWizardSharedData>({
      templateId: isDraftEdit ? (draftTemplateId ?? undefined) : undefined,
      templatesFetched: templatesFetched ?? false,
      templateOptions: templateOptions ?? [],
      templateAllFields: [],
      selectedClientTypeCode: "",
      selectedClientPorterId: 1,
      selectedClientMasterId: clientMasterId ?? (isDraftEdit ? 0 : 0),
      clientMasterIds: [],
      clientsAllData: [],
      groupClientsData: [],
      contractInfoEditorMessage: "",
      contractInfoEditorData: {},
      templateFieldsMapping: {},
      saveApiProgress: false,
      validations: {},
      isEditorLoading: false,
      sync: isDraftEdit ? false : true,
      currentPage: isDraftEdit ? "fillInfo" : "selectTemplate",
      disablePreview: true,
    });

  // Ref to store last updated field in form
  const lastFocusedFieldRef = useRef<string | null>(null);
  const prevFocusedFieldRef = useRef<string | null>(null);

  const editorReadyRef = useRef<boolean>(sharedData.isEditorLoading);
  useEffect(() => {
    editorReadyRef.current = sharedData.isEditorLoading;
  }, [sharedData.isEditorLoading]);

  const syncRef = useRef<boolean | null>(null);
  useEffect(() => {
    syncRef.current = sharedData.sync;
  }, [sharedData.sync]);

  const syncModeRef = useRef<"tab" | "manual">("manual");

  const [engagementLetterWizardConfig] = useState<WizardPageConfig[]>(() => {
    if (isDraftEdit) {
      return [
        {
          key: "fillInfo",
          label: "Fill Information",
          show: true,
          order: 1,
          props: { lastFocusedFieldRef, prevFocusedFieldRef, syncRef },
          validate: false,
        },
      ];
    }
    return [
      {
        key: "selectTemplate",
        label: "Select Template",
        show: true,
        order: 1,
        props: { title: "Choose a template" },
        validate: true,
      },
      !clientMasterId && {
        key: "selectClient",
        label: "Select Client",
        show: true,
        order: 2,
        validate: true,
      },
      {
        key: "fillInfo",
        label: "Fill Information",
        show: true,
        order: 4,
        props: { lastFocusedFieldRef, prevFocusedFieldRef, syncRef },
        validate: false,
      },
      {
        key: "addendums",
        label: "Addendums",
        show: false,
        order: 5,
        validate: false,
      },
    ].filter(Boolean) as WizardPageConfig[];
  });

  useEffect(() => {
    const stepConfig = engagementLetterWizardConfig[activeStep];
    if (stepConfig) {
      setSharedData((prev) => ({
        ...prev,
        currentPage: stepConfig.key,
      }));
    }
  }, [activeStep, engagementLetterWizardConfig]);

  useEffect(() => {
    if (sharedData.currentPage !== "selectTemplate" || clientMasterId) return;
    if (sharedData.allClientsData && sharedData.allClientsData.length > 0)
      return;

    fetchAllClients(setSharedData);
  }, [sharedData.currentPage]);

  // Editor related states
  const [editorLoader, setEditorLoader] = useState<boolean>();
  const [elBase64Data, setElBase64Data] = useState<any>();
  const [elOriginalBase64Data, setElOriginalBase64Data] = useState<any>();
  const [templateRuleDetails, setTemplateRuleDetails] = useState<any>();
  const [editorContent, setEditorContent] = useState<any>();
  const [getEditorContentFlag, setGetEditorContentFlag] =
    useState<boolean>(false);
  const savingAsDraftRef = useRef<boolean>(false);

  const [editorContainerScope, setEditorContainerScope] =
    useState<DocumentEditorContainer>();

  const memoizedFieldsMapping = useMemo(() => {
    return sharedData.templateFieldsMapping;
  }, [
    ["selectTemplate", "selectClient", "fillInfo"].includes(
      engagementLetterWizardConfig[activeStep]?.key
    ) && sharedData.templateFieldsMapping,
  ]);

  const [resizeTrigger, setResizeTrigger] = useState<number>(0);
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const [warningModalData, setWarningModalData] = useState<
    Record<number, string[]>
  >([]);

  // Utility: check if fields mapping has changed
  const attemptSyncTemplateFieldsMapping = async () => {
    const selectedClientId = sharedData.selectedClientMasterId;
    const currentFields = sharedData.contractInfoEditorData[selectedClientId];

    if (!currentFields || !Array.isArray(currentFields)) return null;

    const updated = getUpdatedTemplateFieldsMapping(
      sharedData.templateFieldsMapping,
      currentFields
    );

    return isEqual(sharedData.templateFieldsMapping, updated) ? null : updated;
  };

    // Utility: check if content blocks changed
    const attemptSyncTemplateContentBlocks = async () => {
      const updatedContentBlocks = sharedData?.updatedContentBlocks;
      return isEqual(updatedContentBlocks, sharedData?.templateFieldsMapping?.ContentBlocks)
        ? null
        : updatedContentBlocks;
    };
    
    useEffect(() => {
      if (!sharedData.sync) return;

      const doBackendMerge = async () => {
        try {
          let [contentBlocksChanged, updatedMapping] = await Promise.all([
            attemptSyncTemplateContentBlocks(),
            attemptSyncTemplateFieldsMapping(),
          ]);
          if (!updatedMapping && !contentBlocksChanged) {
            setSharedData((prev) => ({ ...prev, sync: false }));
            return;
          }
          
          const effectiveMapping = updatedMapping || sharedData.templateFieldsMapping;
  
          if(contentBlocksChanged)
            effectiveMapping.ContentBlocks = contentBlocksChanged;

          setEditorLoader(prev => !prev);

          const formattedSelectedFields = prepareFieldsForMailMerge(
            updatedMapping?.SelectedFields ?? effectiveMapping?.SelectedFields,
            sharedData.elForms
          );
          const mergedBase64 = await mailMerge(
            sharedData.templateId ?? 0,
            contentBlocksChanged ?? effectiveMapping?.ContentBlocks,
            updatedMapping?.Rules ?? effectiveMapping?.Rules,
            formattedSelectedFields,
            elOriginalBase64Data
          );

          if (mergedBase64) {
            setElBase64Data(mergedBase64);
          }

          if(effectiveMapping) {
            setSharedData((prev) => ({
              ...prev,
              templateFieldsMapping: effectiveMapping,
            }));
          }
        } catch (err) {
          console.error("Backend merge failed:", err);
        } finally {
          setSharedData((prev) => ({ ...prev, sync: false }));
        }
      };

      doBackendMerge();
    }, [sharedData.sync]);

  /**
   * Syncs contractInfoEditorData values into templateFieldsMapping
   * after a focus is lost.
   */
  useEffect(() => {
    const handleFocusInChange = () => {
      const prev = prevFocusedFieldRef?.current;
      const current = lastFocusedFieldRef?.current;

      if (syncModeRef.current === "tab") {
        if (prev && current && prev !== current) {
          attemptSyncTemplateFieldsMapping();
        }
      }
    };

    window.addEventListener("focusin", handleFocusInChange);
    return () => {
      window.removeEventListener("focusin", handleFocusInChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedData.selectedClientMasterId, sharedData.contractInfoEditorData]);

  /**
   * Fetch wizard data when templateId changes
   */
  useEffect(() => {
    if (sharedData.templateId === undefined) return;

      const fetchWizardData = async () => {
        setEditorLoader(true);
        
        const selectedId = sharedData.selectedClientMasterId;
        const clientData = sharedData.clientsAllData?.[selectedId];

        const data = await fetchWizardPageData(
          sharedData.templateId!,
          clientData,
          isDraftEdit ? draftProcessedTemplateId : undefined
        );

        setSharedData((prev) => ({
          ...prev,
          templateAllFields: data.TemplateFieldMappings,
          contractInfoEditorMessage: "Loading your form.",
          contractInfoEditorData: {},
          elForms: data.ElForms,
          templateFieldsMapping: data.TemplateFieldMappings,
        }));

      if (data.ElForms.length === 0) {
        setSharedData((prev) => ({
          ...prev,
          contractInfoEditorMessage:
            "This feature has not been configured for the selected template. Please complete the configuration to use this feature.",
        }));
      }

        setTemplateRuleDetails(data.TemplateRuleDetails);
        setElBase64Data(data.MergeTemplateContent.Base64Data ?? data.MergeTemplateContent.OriginalBase64Data);
        setElOriginalBase64Data(data.MergeTemplateContent.OriginalBase64Data);
        
        setEditorLoader(false);
      };

    fetchWizardData();
  }, [sharedData.templateId]);

  useEffect(() => {
    const run = async () => {
      const selectedId = sharedData.selectedClientMasterId;
      const clientData = sharedData.clientsAllData?.[selectedId];
      if (!selectedId || !clientData) return;

      let baseFields: any[] = [];

      if (sharedData.elForms?.length > 0) {
        baseFields = sharedData.elForms[0].InputStructure.fields.map((f: any) => {
          let val = f.allfields_code
            ? clientData[f.allfields_code] ?? f.value
            : f.value;
          if (f.type === "date") {
            val = formatByConfig(val, f);
            if (!val && f.defaultToToday) {
              const today = format(new Date(), getDisplayFmt(f));
              val = today;
            }
          }
          if (f.type === "select" && f.format) {
            val = applyNameFormat(String(val || ""), f.format);
          }

          return { ...f, value: val };
        });
      }
        
      // Apply saved draft content block states if available
      const baseContentBlocks = sharedData.templateAllFields?.ContentBlocks;
      let resolvedContentBlocks = baseContentBlocks;
      if (isDraftEdit && draftContentBlockStatesRef.current && Array.isArray(baseContentBlocks)) {
        const cbStates = draftContentBlockStatesRef.current;
        resolvedContentBlocks = baseContentBlocks.map((block: any) =>
          block.name && cbStates.hasOwnProperty(block.name)
            ? { ...block, enabled: cbStates[block.name] }
            : block
        );
        draftContentBlockStatesRef.current = null;
      }

      setSharedData((prev) => ({
        ...prev,
        contractInfoEditorData: {
          ...prev.contractInfoEditorData,
          [selectedId]: baseFields
        },
        updatedContentBlocks: resolvedContentBlocks,
      }));

      if (sharedData.currentPage === "selectClient") {
        setSharedData((prev) => ({
          ...prev,
          sync: true
        }));
      }
      else if (sharedData.currentPage === "selectTemplate") {
        const updatedMapping = getUpdatedTemplateFieldsMapping(
            sharedData.templateFieldsMapping,
            baseFields
        );
        setSharedData((prev) => ({
          ...prev,
          templateFieldsMapping: updatedMapping,
        }));
      }
    };

    run();
  }, [
    sharedData.clientsAllData,
    sharedData.elForms,
    sharedData.templateAllFields,
    templateRuleDetails,
  ]);

  const draftContentBlockStatesRef = useRef<Record<string, boolean> | null>(null);

  // In draft edit mode: after wizard data loads, fetch and apply saved field values
  useEffect(() => {
    if (!isDraftEdit || !draftProcessedTemplateId || !clientMasterId) return;
    if (!sharedData.templateFieldsMapping?.SelectedFields?.length) return;
    if (sharedData.clientsAllData?.[clientMasterId]) return;

    const loadDraftFieldValues = async () => {
      try {
        const records = await fetchProcessedTemplateClientDetails(draftProcessedTemplateId);

        const CB_PREFIX = "__cb__";
        const fieldRecords = records.filter((rec) => !rec.FieldCode?.startsWith(CB_PREFIX));
        const cbRecords = records.filter((rec) => rec.FieldCode?.startsWith(CB_PREFIX));

        if (cbRecords.length > 0) {
          const cbStates: Record<string, boolean> = {};
          cbRecords.forEach((rec) => {
            const blockName = rec.FieldCode!.slice(CB_PREFIX.length);
            cbStates[blockName] = rec.ClientFieldValue === "true";
          });
          draftContentBlockStatesRef.current = cbStates;
        }

        // Convert saved records to { allfields_code: value } for form population
        const clientDataLike: Record<string, any> = {};
        fieldRecords.forEach((rec) => {
          const name = rec.FieldCode;
          const value = rec.ClientFieldValue;
          if (name) {
            clientDataLike[name] = value;
          }
        });
        setSharedData((prev) => ({
          ...prev,
          selectedClientMasterId: clientMasterId,
          clientMasterIds: [clientMasterId],
          clientsAllData: { [clientMasterId]: clientDataLike },
        }));
      } catch (err) {
        console.error("Failed to load draft field values:", err);
      }
    };

    loadDraftFieldValues();
  }, [isDraftEdit, draftProcessedTemplateId, clientMasterId, sharedData.templateFieldsMapping?.SelectedFields]);

  useEffect(() => {
    // In draft edit mode, client data comes from saved draft values (not live client API)
    if (isDraftEdit) return;

    // Prefer prop if available, else take from sharedData
    const id = clientMasterId || sharedData.selectedClientMasterId;
    if (!id) return;

    // If already cached, don't fetch again
    if (sharedData.clientsAllData[id]) {
      // nothing to fetch, but replacement effect will handle it
      return;
    }

    const fetchAndStoreClient = async () => {
      try {
        if(sharedData.currentPage === "selectClient")
          setEditorLoader(prev => !prev);
        
        const clientData = await fetchClientDetails(id);
        setSharedData((prev) => ({
          ...prev,
          selectedClientMasterId: id,
          clientMasterIds: [id],
          clientsAllData: {
            [id]: clientData,
          }
        }));
      } catch (err) {
        console.error("Error fetching client details:", err);
      }
    };

    fetchAndStoreClient();
  }, [clientMasterId, sharedData.selectedClientMasterId]);

  useEffect(() => {
    if (!editorContent) return;

    const clientId = sharedData.selectedClientMasterId;
    const fieldMapping = getUpdatedTemplateFieldsMapping(
      sharedData.templateFieldsMapping,
      sharedData.contractInfoEditorData[clientId] || []
    );

    const finalFieldsForSave = Array.isArray(fieldMapping.SelectedFields)
      ? prepareFieldsForMailMerge(
          fieldMapping.SelectedFields,
          sharedData.elForms
        )
      : [];

    const selectedFieldsMap = finalFieldsForSave.reduce(
      (acc: Record<string, any>, item: { name: string; value: any }) => {
        if (
          item.name &&
          item.value !== undefined &&
          item.value !== null &&
          item.value !== ""
        ) {
          acc[item.name] = item.value;
        }
        return acc;
      },
      {}
    );

    const templateId = sharedData.templateId ?? 0;
    const templateOption = sharedData.templateOptions.find(
      (option: any) => option.value === templateId
    );

    const recipientsRaw = templateOption?.recipients;
    const parsedOptions = Array.isArray(recipientsRaw)
      ? recipientsRaw
      : typeof recipientsRaw === "string"
      ? JSON.parse(recipientsRaw || "[]")
      : [];

    const isDraft = savingAsDraftRef.current;
    savingAsDraftRef.current = false;

    saveEngagementLetter(
      templateId,
      clientId,
      selectedFieldsMap,
      sharedData?.updatedContentBlocks,
      editorContent,
      parsedOptions,
      onHide,
      isDraft,
      isDraftEdit ? draftProcessedTemplateId : undefined
    )
      .then(() => {
        setSharedData((prev) => ({
          ...prev,
          saveApiProgress: false,
          draftApiProgress: false,
          isEditorLoading: false,
        }));
      })
      .catch((err) => {
        console.error("Error saving engagement letter:", err);
        setSharedData((prev) => ({
          ...prev,
          saveApiProgress: false,
          draftApiProgress: false,
          isEditorLoading: false,
        }));
      })
      .finally(() => {
      });
  }, [editorContent]);

  /**
   * Finds empty required fields for the current client.
   */
  const findEmptyFields = (
    templateAllFieldsMapping: Record<string, any[]>,
    sharedData: any
  ): string[] => {
    if (!templateAllFieldsMapping) return [];

    const emptyFields: string[] = [];
    const baseFields =
      sharedData.contractInfoEditorData?.[sharedData.selectedClientMasterId];
    const selectedFields = templateAllFieldsMapping["SelectedFields"];

    if (Array.isArray(selectedFields)) {
      selectedFields.forEach((item) => {
        const currentField = baseFields?.find(
          (f: any) => f.allfields_code === item.code
        );
        const isRequired = currentField?.validation?.some(
          (rule: any) => rule.type === "required"
        );
        if (!isRequired) return;

        const parentField = baseFields?.find(
          (f: any) =>
            String(f.allfields_code) === String(currentField?.dependsOn)
        );
        const shouldValidate = parentField
          ? String(parentField.value).toLowerCase() === "true"
          : true;

        if (shouldValidate && (item.value === "" || item.value === null)) {
          emptyFields.push(item.label || item.name);
        }
      });
    }
    return emptyFields;
  };

  useEffect(() => {
    if (
      !sharedData.isEditorLoading &&
      !sharedData.sync &&
      sharedData.templateFieldsMapping?.SelectedFields?.length
    ) {
      setSharedData((prev) => ({ ...prev, disablePreview: true }));
    }
  }, [sharedData.isEditorLoading, sharedData.sync]);

  /**
   * Base normalization (used for general field mapping)
   * - Treats "", null, undefined, and false as equivalent empty values.
   * - Sorts keys to ignore ordering differences.
   * - Strips transient UI-only properties like "isFocused", "tempId", "data".
   */
  const normalizeForComparison = (input: any): any => {
    if (Array.isArray(input)) {
      return input.map(normalizeForComparison);
    }

    if (input && typeof input === "object") {
      const normalized: Record<string, any> = {};
      Object.keys(input)
        .sort()
        .forEach((key) => {
          if (["isFocused", "tempId", "data"].includes(key)) return;
          normalized[key] = normalizeForComparison(input[key]);
        });
      return normalized;
    }

    // Handle strings with "false" or whitespace-only HTML
    if (typeof input === "string") {
      const trimmed = input.trim();

      // Treat "false", "null", "undefined" as empty
      if (["false", "null", "undefined"].includes(trimmed.toLowerCase()))
        return "";

      // Strip redundant whitespace in HTML
      if (trimmed.startsWith("<") && trimmed.endsWith(">")) {
        return trimmed.replace(/\s+/g, " "); // normalize spaces in HTML
      }

      return trimmed;
    }

    // Normalize other falsy values
    if (input === null || input === undefined) return "";
    if (typeof input === "boolean") return input ? "true" : "";
    return input;
  };

  /**
   * Specialized normalization for content blocks.
   * - Keeps 'enabled' always present (false if missing).
   * - Normalizes HTML and string values (trims, merges whitespace, treats "false"/"null"/"undefined" as empty).
   */
  const normalizeForContentBlocks = (input: any): any => {
    if (Array.isArray(input)) {
      return input.map(normalizeForContentBlocks);
    }

    if (input && typeof input === "object") {
      const normalized: Record<string, any> = {};

      // Always include 'enabled' (default false if missing)
      const keys = new Set([...Object.keys(input), "enabled"]);
      Array.from(keys)
        .sort()
        .forEach((key) => {
          if (["isFocused", "tempId", "data"].includes(key)) return;

          if (key === "enabled") {
            normalized[key] = input[key] ? true : false;
            return;
          }

          normalized[key] = normalizeForContentBlocks(input[key]);
        });
      return normalized;
    }

    if (typeof input === "string") {
      const trimmed = input.trim();

      // Treat these textual falsy forms as empty
      if (["false", "null", "undefined"].includes(trimmed.toLowerCase()))
        return "";

      // Normalize HTML whitespace
      if (trimmed.startsWith("<") && trimmed.endsWith(">")) {
        return trimmed.replace(/\s+/g, " ");
      }

      return trimmed;
    }

    if (input === "" || input === null || input === undefined) return "";
    if (typeof input === "boolean") return input ? "true" : "";
    return input;
  };

  useEffect(() => {
    if (!sharedData.selectedClientMasterId) return;
    const selectedId = sharedData.selectedClientMasterId;
    const currentFields = sharedData.contractInfoEditorData[selectedId];
    if (!currentFields || !Array.isArray(currentFields)) {
      return;
    }

    const beforeBlocksNorm = normalizeForContentBlocks(
      sharedData?.templateFieldsMapping?.ContentBlocks
    );
    const afterBlocksNorm = normalizeForContentBlocks(
      sharedData?.updatedContentBlocks
    );

    const beforeFieldsNorm = normalizeForComparison(
      sharedData.templateFieldsMapping
    );
    const afterFieldsNorm = normalizeForComparison(
      getUpdatedTemplateFieldsMapping(
        sharedData.templateFieldsMapping,
        currentFields
      )
    );
    
    const fieldsEqual = isEqual(beforeFieldsNorm, afterFieldsNorm);
    
    const blocksEqual = isEqual(beforeBlocksNorm, afterBlocksNorm);
    const hasChanges = !fieldsEqual || !blocksEqual;

    setSharedData((prev) => {
      if (prev.disablePreview !== hasChanges) return prev;
      return { ...prev, disablePreview: !hasChanges };
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sharedData.contractInfoEditorData,
    sharedData.templateFieldsMapping,
    sharedData.updatedContentBlocks,
  ]);

  const triggerSaveAsDraft = async () => {
    savingAsDraftRef.current = true;
    setSharedData((prev) => ({
      ...prev,
      draftApiProgress: true,
      isEditorLoading: true,
    }));
    setEditorContent(true);
  };

  /**
   * Handles synchronization, validation, and document extraction for Engagement Letter submission.
   */
  const triggerSyncAndSubmit = async () => {
    const clientId = sharedData.selectedClientMasterId;
    const fieldMapping = getUpdatedTemplateFieldsMapping(
      sharedData.templateFieldsMapping,
      sharedData.contractInfoEditorData[clientId] || []
    );
    const emptyFields = findEmptyFields(fieldMapping, sharedData);

    if (emptyFields.length > 0) {
      setWarningModalData({ [clientId]: emptyFields });
      setShowWarningModal(true);
      setSharedData((prev) => ({
        ...prev,
        saveApiProgress: false,
        isEditorLoading: false,
      }));
      return;
    }

    setSharedData((prev) => ({
      ...prev,
      saveApiProgress: true,
      isEditorLoading: true,
    }));

    setEditorContent(true);
  };

  return (
    <>
      <WarningModal
        show={showWarningModal}
        warningModalData={warningModalData}
        onHide={() => setShowWarningModal(false)}
      />
      <Modal
        show={show}
        onHide={() => onHide()}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        backdropClassName="custom-backdrop"
        className={styles.createEngagementLetterWizardModal}
        centered
        scrollable={false}
        keyboard={false}
      >
        <Modal.Header
          className={styles.createEngagementLetterWizardHeader}
          closeButton
        >
          <WizardHeaderPagination
            config={engagementLetterWizardConfig}
            purpose={"Engagement Letter"}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            sharedData={sharedData}
            setSharedData={setSharedData}
            onComplete={triggerSyncAndSubmit}
            onSaveAsDraft={triggerSaveAsDraft}
          />
        </Modal.Header>
        <Modal.Body>
          <Row className={styles.leftRightView}>
            <Col>
              <ReflexContainer orientation="vertical">
                <ReflexElement
                  minSize={200}
                  flex={0.5}
                  onStopResize={() => setResizeTrigger((prev) => prev + 1)}
                  className={styles.engagementWizardReflexElement}
                >
                  <WizardPageController<EngagementLetterWizardSharedData>
                    config={engagementLetterWizardConfig}
                    componentMap={createNewEngagementLetterComponentMap}
                    sharedData={sharedData}
                    setSharedData={setSharedData}
                    activeStep={activeStep}
                  />
                </ReflexElement>

                <ReflexSplitter className={styles.wizardPageSplitter}>
                  <Icon.DistributeHorizontal
                    className={styles.wizardPageSplitterIcon}
                  />
                </ReflexSplitter>

                <ReflexElement className={styles.engagementWizardReflexElement}>
                  <CreateOrEditEditorComponent
                    clientMasterId={sharedData.selectedClientMasterId}
                    templateId={sharedData.templateId!}
                    docfilename={
                      sharedData.templateOptions.find(
                        (option) => option.value === sharedData.templateId
                      )?.filename!
                    }
                    showheader={false}
                    edittype={"Template"}
                    forwizard={true}
                    basedata={elBase64Data}
                    dataloader={editorLoader}
                    restrictediting={false}
                    resize={resizeTrigger}
                    setSharedData={setSharedData}
                    sharedData={sharedData}
                    setEditorContainerScope={setEditorContainerScope}
                  />
                </ReflexElement>
              </ReflexContainer>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CreateEngagementLetterWizard;
